import apiClient from '../axios.js';

const CHATDESK_DEVICE_ID_KEY = 'chatdesk_device_id';

export function getChatDeskDeviceId() {
  let deviceId = localStorage.getItem(CHATDESK_DEVICE_ID_KEY);
  if (deviceId && /^[A-Za-z0-9_-]{16,128}$/.test(deviceId)) return deviceId;
  deviceId = globalThis.crypto?.randomUUID?.()
    || `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(CHATDESK_DEVICE_ID_KEY, deviceId);
  return deviceId;
}

function withDeviceId(params = {}) {
  return { ...params, deviceId: getChatDeskDeviceId() };
}

export const chatdeskService = {
  async getSubscription() {
    const response = await apiClient.get('/chatdesk/subscription');
    return response;
  },

  async listAssignableAgents() {
    const response = await apiClient.get('/chatdesk/assignable-agents');
    return response;
  },

  async acquireRealtimeSession(deviceId) {
    const response = await apiClient.post('/chatdesk/realtime-session', { deviceId });
    return response;
  },

  async listCannedReplies(params = {}) {
    const response = await apiClient.get('/chatdesk/canned-replies', { params });
    return response;
  },

  async getCannedReply(id) {
    const response = await apiClient.get(`/chatdesk/canned-replies/${id}`);
    return response;
  },

  async createCannedReply(payload) {
    const response = await apiClient.post('/chatdesk/canned-replies', payload);
    return response;
  },

  async updateCannedReply(id, payload) {
    const response = await apiClient.put(`/chatdesk/canned-replies/${id}`, payload);
    return response;
  },

  async deleteCannedReply(id) {
    const response = await apiClient.delete(`/chatdesk/canned-replies/${id}`);
    return response;
  },

  async listSessions(params = {}) {
    const response = await apiClient.get('/chatdesk/sessions', { params: withDeviceId(params) });
    return response;
  },

  async getSession(sessionId) {
    const response = await apiClient.get(`/chatdesk/sessions/${sessionId}`, {
      params: withDeviceId()
    });
    return response;
  },

  async getMessages(sessionId, params = {}) {
    const response = await apiClient.get(`/chatdesk/sessions/${sessionId}/messages`, {
      params: withDeviceId(params)
    });
    return response;
  },

  async getMemberContext(sessionId) {
    const response = await apiClient.get(`/chatdesk/sessions/${sessionId}/member-context`, {
      params: withDeviceId()
    });
    return response;
  },

  async getConsumerHistory(sessionId, { historyType, historySessionId }) {
    const response = await apiClient.get(`/chatdesk/sessions/${sessionId}/member-context`, {
      params: withDeviceId({ historyType, historySessionId })
    });
    return response;
  },

  async claimSession(sessionId) {
    const response = await apiClient.post(
      `/chatdesk/sessions/${sessionId}/claim`,
      null,
      { params: withDeviceId() }
    );
    return response;
  },

  async transferSession(sessionId, toAccountId) {
    const response = await apiClient.post(
      `/chatdesk/sessions/${sessionId}/transfer`,
      { toAccountId },
      { params: withDeviceId() }
    );
    return response;
  },

  async replySession(sessionId, payload) {
    const response = await apiClient.post(
      `/chatdesk/sessions/${sessionId}/reply`,
      payload,
      { params: withDeviceId() }
    );
    return response;
  },

  async releaseSession(sessionId) {
    const response = await apiClient.post(
      `/chatdesk/sessions/${sessionId}/release`,
      null,
      { params: withDeviceId() }
    );
    return response;
  },

  async closeSession(sessionId) {
    const response = await apiClient.post(
      `/chatdesk/sessions/${sessionId}/close`,
      null,
      { params: withDeviceId() }
    );
    return response;
  },

  /**
   * 從對話一鍵建立客服工單（ChatDesk 內建）
   * @param {string} sessionId
   * @param {Object} [payload]
   */
  async createTicket(sessionId, payload = {}) {
    const response = await apiClient.post(
      `/chatdesk/sessions/${sessionId}/tickets`,
      payload,
      { params: withDeviceId() }
    );
    return response;
  },

  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response;
  }
};

export default chatdeskService;
